import { filter } from 'rxjs/operators';
import { Component, HostListener, AfterViewInit, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef, TemplateRef } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { InvolvementsComponent } from '../../involvements/involvements/involvements.component';
import { OutgoingJobsIndexComponent } from '../../outgoing/outgoing-jobs-index/outgoing-jobs-index.component';

import { ActionCableService } from '../../../services/common/action-cable.service';
import { AuthService } from '../../../services/common/auth.service';
import { DocumentStoreService } from '../../../services/documents/document-store.service';
import { MatterService } from '../../../services/matters/matter.service';
import { MatterStoreService } from '../../../services/matters/matter-store.service';
import { TinymceService } from '../../../services/common/tinymce.service';
import { UserService } from '../../../services/users/user.service';
import { NotyService } from '../../../services/common/noty.service';

import { MatterHistoriesComponent } from '../matter-histories/matter-histories.component';
import { MatterSidebarComponent } from '../matter-sidebar/matter-sidebar.component';

@Component({
  selector: 'app-matter-show',
  templateUrl: './matter-show.component.html',
  styleUrls: ['./matter-show.component.css'],
  providers: [DocumentStoreService, MatterStoreService, TinymceService],
})
export class MatterShowComponent implements AfterViewInit, OnInit, OnDestroy {
  /**
   * Indicates that the component has been destroyed
   * @type {boolean}
   */
  componentDestroyed: boolean = false;

  /**
   * List of docs to show as tabs in documents section
   * @type {Array<any>}
   */
  docs: Array<any> = [];

  /**
   * Current document tab id inside document section
   * @type {string}
   */
  documentsTabId: string = 'list_documents';

  /**
   * Indicates if documents list is the active tab inside documents section
   * @type {boolean}
   */
  listDocumentTabActive: boolean = true;

  /**
   * Indicates if matter page has started to load
   * @type {boolean}
   */
  loadingMatterPage: boolean = false;

  /**
   * Matter id of current matter page
   * @type {number}
   */
  matter_id: number;

  /**
   * Matter info to be shown in the notice area
   * @type {any}
   */
  matter_info: any = {};

  /**
   * Used to set matter history reply parent id and matter id for the matter history form in the modal
   * @type {any}
   */
  modalParams: any = {};

  /**
   * Indicates if new document tab is active inside documents section
   * @type {boolean}
   */
  newDocumentTabActive: boolean = false;

  /**
   * Current user permissions on this page
   * @type {any}
   */
  permissions: any = {};

  /**
   * Message shown when the initial matter information request fails
   */
  matterInfoLoadError: string | null = null;

  /**
   * Subscription to router events to catch page changes and matter id changes in the URL
   * @type {Subscription}
   */
  routerEventsSubscription: Subscription;

  /**
   * Matter channel subscription for conflict notifications
   */
  matterChannelSubscription: any;

  showDocumentsSectionAdditionalTabs: boolean = false;

  /**
   * Indicates if new involvement form is to be shown
   * @type {boolean}
   */
  showNewInvolvementForm: boolean = false;

  /**
   * Indicates if new matter history form is to be shown
   * @type {boolean}
   */
  showNewMatterHistoryForm: boolean = false;

  /**
   * Accordion collapse state for Details tab sections
   */
  noticeCollapsed: boolean = false;
  notesCollapsed: boolean = false;
  contactsCollapsed: boolean = false;
  historiesCollapsed: boolean = false;
  retainersCollapsed: boolean = false;

  accountingTabLoaded: boolean = false;

  /**
   * Indicates if details section is to be shown
   * @type {boolean}
   */
  showDetails: boolean = true;

  /**
   * Indicates if documents section is to be shown
   * @type {boolean}
   */
  showDocuments: boolean = false;

  /**
   * Indicates if status section is to be shown
   * @type {boolean}
   */
  showStatus: boolean = false;

  retainerRequest: any = { amount: null, description: '' };
  retainerRequests: Array<any> = [];
  retainerRequestsLoading: boolean = false;
  retainerRequestSaving: boolean = false;

  /**
   * Indicates if sidebar is expanded to the left
   * @type {boolean}
   */
  sidebarMaximized: boolean = false;

  /**
   * Indicates if sidebar is open in small screens
   * @type {boolean}
   */
  sidebarOpen: boolean = false;

  /**
   * Reference to involvements component
   * @param {[type]} 'involvements' variable name
   */
  @ViewChild('involvements', { static: false }) involvementsComponent: InvolvementsComponent;

  /**
   * Reference to sidebar HTML element
   * @param {[type]} 'matterSidebarElem' variable name
   */
  @ViewChild('matterSidebarElem', { static: false }) matterSidebarElement: ElementRef;

  /**
   * Reference to sidebar component
   * @param {[type]} 'matterSidebar' variable name
   */
  @ViewChild('matterSidebar', { static: false }) matterSidebarComponent: MatterSidebarComponent;

  /**
   * Reference to outgoing jobs index component
   * @param {[type]} 'outgoingJobsIndex' variable name
   */
  @ViewChild('outgoingJobsIndex', { static: false }) outgoingJobsIndexComponent: OutgoingJobsIndexComponent;

  /**
   * Reference to matter histories component
   * @param {[type]} 'matter_histories' variable name
   */
  @ViewChild('matter_histories', { static: false }) matterHistoriesComponent: MatterHistoriesComponent;

  /**
   * Reference to matterhistory reply modal
   * @param {[type]} 'matter_history_form' variable name
   */
  @ViewChild('matter_history_form', { static: false }) matterHistoryReplyModal: TemplateRef<any>;

  /**
   * Constructor
   * @param {AuthService}          private authService          Service to access current user info and permissions
   * @param {ChangeDetectorRef}    public  changeDetectorRef    Angular service to trigger change detection in variable values and in HTML
   * @param {DocumentStoreService} private documentStoreService Service to share document related objects in this component between child components
   * @param {Location}             private location             Angular service to access and modify page location
   * @param {MatterService}        private matterService        Service to access matters controller
   * @param {MatterStoreService}   public  matterStoreService   Service to share matter related objects in this component between child components
   * @param {NgbModal}             private modalService         Ngb modal service to show modals
   * @param {ActivatedRoute}       private route                Angular current route and params
   * @param {Router}               private router               Angular service to change page or URL and to subscribe to any page or URL changes
   * @param {TinymceService}       private tinymceService       Service to initialize tinymce editors
   * @param {UserService}          private userService          Service to access users controller
   */
  constructor(
    private actionCableService: ActionCableService,
    private authService: AuthService,
    public changeDetectorRef: ChangeDetectorRef,
    private documentStoreService: DocumentStoreService,
    private location: Location,
    private matterService: MatterService,
    public matterStoreService: MatterStoreService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private router: Router,
    private tinymceService: TinymceService,
    private userService: UserService,
    private notyService: NotyService
  ) {
    this.matterStoreService.matterShowComponent = this;
    this.tinymceService.tinymce_schema_init();
    this.routerEventsSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      this.loadMatterPage();
    });
  }

  // Component Lifecycle functions

  /**
   * Called when component is initialized
   */
  ngOnInit() {
    this.content_sidebar_scroll();
    this.loadMatterPage();
  }

  /**
   * Called when component is destroyed
   */
  ngOnDestroy() {
    this.componentDestroyed = true;
    this.routerEventsSubscription.unsubscribe();
    if (this.matterChannelSubscription) {
      this.matterChannelSubscription.unsubscribe();
      this.matterChannelSubscription = null;
    }
    if(this.userService.navbarComponent){
      this.userService.navbarComponent.clearSecondMenu();
    }
    this.matterStoreService.matterShowComponent = null;
    this.matterStoreService.billingsIndexComponent = null;
  }

  /**
   * Called after view is initialized
   */
  ngAfterViewInit() {
    setTimeout(() => {
      this.content_sidebar_scroll();
    }, 1000);
  }

  // Event Listeners

  /**
   * Triggers on window scroll event
   * @param {scroll'} 'window  window
   * @param {[type]}  '$event' scroll event
   */
  @HostListener('window:scroll', ['$event'])
  window_scroll(event: Event) {
    this.content_sidebar_scroll();
  }

  /**
   * Triggers on window resize event
   * @param {resize'} 'window  window object
   * @param {[type]}  '$event' resize event
   */
  @HostListener('window:resize', ['$event'])
  window_resize(event: Event) {
    this.content_sidebar_scroll();
  }

  // Component Functions

  /**
   * Add a doc to show in documents section
   * @param {any} doc document to show
   */
  addToDocumentShow(doc: any) {
    for (let doc_obj of this.docs)
      if (doc_obj.id == doc.id) {
        for (let doc of this.docs) doc.active = false;
        this.listDocumentTabActive = false;
        this.newDocumentTabActive = false;
        doc.active = true;
        return;
      }
    this.docs.push(doc);
    for (let doc of this.docs) doc.active = false;
    this.listDocumentTabActive = false;
    this.newDocumentTabActive = false;
    doc.active = true;
  }

  /**
   * Set title for document form
   * @return {string} Document form title
   */
  documentFormTitle(): string {
    return this.documentStoreService.documentFormComponent!.doc && this.documentStoreService.documentFormComponent!.doc.id ? 'Editing Document' : 'New Document';
  }

  /**
   * Process involvement form result
   * @param {[type]} event result value
   */
  involvementFormResult(event: string) {
    if (event == 'Form Cancelled') this.showNewInvolvementForm = false;
    else if (event == 'Involvement created' || event == 'Involvement updated') {
      this.showNewInvolvementForm = false;
      this.involvementsComponent.loadInvolvements();
    } else if (event == 'Involvement updated') this.showNewInvolvementForm = false;
  }

  /**
   * Loads matter info only
   */
  loadMatterInfo() {
    this.matterService.getMatterInfo(this.matter_id).then((response_json) => {
      if (response_json.matter_info) this.matter_info = response_json.matter_info;
    });
  }

  /**
   * Load matter page
   */
  loadMatterPage() {
    console.log('Navigation end');
    this.modalParams = {};
    this.matter_id = Number(this.route.snapshot.params['id']);
    let matter_history_id: number = Number(this.route.snapshot.params['matter_history_id']);
    let matter_history_reply: boolean = this.route.snapshot.data['matter_history_reply'];
    if (matter_history_id && matter_history_reply) {
      this.modalParams.parent_id = matter_history_id;
      this.modalParams.matter_id = this.matter_id;
      this.openMatterHistoryReplyModal();
    }
    let scope: MatterShowComponent = this;
    this.loadingMatterPage = true;
    this.matterInfoLoadError = null;
    this.matterService.getMatterInfo(this.matter_id).then((response_json) => {
      this.loadingMatterPage = false;
      if (!this.route.snapshot.queryParams['document_id']) {
        this.showDetails = false;
        this.showDocuments = false;
        this.showStatus = false;
        setTimeout(() => {
          this.showDetails = true;
        }, 1000);
      }
      if (!response_json || !response_json.matter_info) {
        console.log('Bad matter info fetch result, loading matter info will be attempted again in 20 seconds');
        response_json = [];
        window.setTimeout(() => {
          if (scope && scope.loadMatterPage) scope.loadMatterPage();
        }, 20000);
        throw `Matter info not returned for matter id ${this.matter_id}, response JSON is undefined`;
      }
      this.matter_info = response_json.matter_info;
      this.loadPayloadRetainerRequests();
      this.permissions = this.matter_info.permissions;
      this.authService.getCurrentUserGroups().then((response_json) => {
        this.permissions.is_admin = this.authService.getUser().isAdmin();
        this.permissions.is_manager = this.authService.getUser().isManager();
        this.permissions.is_staff = this.authService.getUser().isStaff();
        if (this.permissions.is_admin || this.permissions.is_matter_user || this.permissions.is_lead_attorney) {
          this.permissions.document_editor = true;
          this.permissions.document_uploader = true;
          this.matterStoreService.documentEditor = true;
          this.matterStoreService.documentUploader = true;
        } else {
          this.permissions.document_editor = false;
          this.permissions.document_uploader = false;
          this.matterStoreService.documentEditor = false;
          this.matterStoreService.documentUploader = false;
        }
        if (this.permissions.is_scanner) {
          this.permissions.document_uploader = true;
          this.matterStoreService.documentUploader = true;
        }
        this.showDocumentsSectionAdditionalTabs = true;
      });
      this.matterStoreService.is_matter_user = this.matter_info.is_matter_user;
      this.userService.navbarComponent.setMatterShowMenu(this.matter_id, this.matter_info.number);
      if (this.matterStoreService.matterAdministrationComponent) {
        this.matterStoreService.matterAdministrationComponent.isArchived = this.matter_info.is_archived;
        if (!this.componentDestroyed) this.matterStoreService.matterAdministrationComponent.changeDetectorRef.detectChanges();
      }
      // Subscribe to matter channel for conflict notifications
      this.subscribeMatterChannel();
    }).catch(() => {
      this.loadingMatterPage = false;
      this.matterInfoLoadError = 'Matter information did not load. Please refresh the page.';
      this.notyService.notify(this.matterInfoLoadError, 'error');
    });
    console.log(this.route.snapshot.queryParams);
  }

  /**
   * Subscribe to matter channel for conflict notifications
   */
  subscribeMatterChannel() {
    // Unsubscribe from previous if exists
    if (this.matterChannelSubscription) {
      this.matterChannelSubscription.unsubscribe();
      this.matterChannelSubscription = null;
    }

    this.actionCableService.cable().then((cable: any) => {
      this.matterChannelSubscription = cable.subscriptions.create(
        { channel: 'MatterChannel', matter_id: this.matter_id },
        {
          connected: () => {
            console.log('Subscribed to MatterChannel for matter', this.matter_id);
          },
          received: (data: any) => {
            if (data.action === 'conflict_found') {
              // Show notification
              this.notyService.notify(data.message, 'warning', 10000);
              // Reload matter info to show freeze banner
              this.loadMatterInfo();
            }
          },
          rejected: () => {
            console.log('MatterChannel subscription rejected');
          },
        }
      );
    });
  }

  /**
   * Process matter history form result
   * @param {[type]} event result value
   */
  matterHistoryFormResult(event: string) {
    if (event == 'Form Cancelled') this.showNewMatterHistoryForm = false;
  }

  loadPayloadRetainerRequests() {
    if (!this.matter_id) return;

    this.retainerRequestsLoading = true;
    this.matterService
      .getPayloadRetainerRequests(this.matter_id)
      .then((response_json) => {
        this.retainerRequests = response_json.retainer_requests || [];
        this.retainerRequestsLoading = false;
      })
      .catch(() => {
        this.retainerRequestsLoading = false;
      });
  }

  requestPayloadRetainer() {
    if (this.retainerRequestSaving) return;

    this.retainerRequestSaving = true;
    this.matterService
      .requestPayloadRetainer(this.matter_id, this.retainerRequest)
      .then((response_json) => {
        this.retainerRequestSaving = false;
        if (response_json.success) {
          this.retainerRequests.unshift(response_json.retainer_request);
          this.retainerRequest = { amount: null, description: '' };
          this.notyService.notify('Retainer request created', 'success');
        } else {
          this.notyService.notify(response_json.error || 'Retainer request failed', 'error');
        }
      })
      .catch((error) => {
        this.retainerRequestSaving = false;
        let message = error && error.error && error.error.error ? error.error.error : 'Retainer request failed';
        this.notyService.notify(message, 'error');
      });
  }

  copyRetainerPaymentUrl(paymentUrl: string) {
    if (!paymentUrl) return;

    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(paymentUrl).then(() => {
        this.notyService.notify('Payment link copied', 'success');
      });
    }
  }

  /**
   * Open matter history reply modal
   */
  openMatterHistoryReplyModal() {
    this.modalService.open(this.matterHistoryReplyModal, { size: 'lg' }).result.then(
      (result) => {
        this.location.go(`/matters/${this.matter_id}`);
        this.matterHistoriesComponent.loadHistories(true);
      },
      (reason) => {
        this.location.go(`/matters/${this.matter_id}`);
        this.matterHistoriesComponent.loadHistories(true);
      }
    );
  }

  /**
   * Remove doc from being showed
   * @param {number}     index Position of document to be removed in the list of tabs
   * @param {boolean =     false}       reloadDocuments Indicats if documents list is to be reload
   */
  removeFromDocumentShow(index: number, reloadDocuments: boolean = false) {
    console.log('matter-show-component.ts docs', this.docs);
    let doc = this.docs[index];
    if (doc && doc.active) {
      doc.active = false;
      this.listDocumentTabActive = true;
    }
    this.docs.splice(index, 1);
    if (reloadDocuments) this.documentStoreService.matterDocumentsListComponent!.loadDocuments();
  }

  /**
   * Scroll to documents section in sidebar
   */
  scrollToSidebarDocuments() {
    if (this.matterSidebarElement && this.matterSidebarComponent && this.matterSidebarComponent.sidebarDocumentElement) this.scrollToSidebar(this.matterSidebarElement.nativeElement, this.matterSidebarComponent.sidebarDocumentElement.nativeElement);
  }

  /**
   * Scroll to sidebar record time section
   */
  scrollToSidebarRecordTime() {}

  getAiDocumentsUrl(): string {
    return this.authService.getAuthenticatedDownloadUrl('/matters/' + this.matter_id + '/ai_documents/new?embedded=1');
  }

  getCardPrintingUrl(): string {
    return this.authService.getAuthenticatedDownloadUrl('/matters/' + this.matter_id + '/card_printing?embedded=1');
  }

  /**
   * Show documents form in documents section
   */
  showDocumentFormTab() {
    for (let doc_obj of this.docs) doc_obj.active = false;
    this.listDocumentTabActive = false;
    this.newDocumentTabActive = true;
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Listen for postMessage from AI document generation iframe
   */
  @HostListener('window:message', ['$event'])
  onAiDocumentMessage(event: MessageEvent) {
    if (event.origin !== window.location.origin) return;

    if (event.data && event.data.type === 'ai_document_created' && event.data.document_id) {
      console.log('AI document created:', event.data.document_id, event.data.document_name);

      // Move from AI Research to Documents before activating the nested editor tab.
      this.showDetails = false;
      this.showStatus = false;
      this.showDocuments = true;
      this.changeDetectorRef.detectChanges();

      this.openAiDocumentInEditor(event.data.document_id);
    }
  }

  /**
   * Open an AI-created or revised document after the Documents tab and its
   * nested editor components have initialized.
   */
  private openAiDocumentInEditor(documentId: number, attempts: number = 0) {
    const documentsList = this.documentStoreService.matterDocumentsListComponent;
    const documentForm = this.documentStoreService.documentFormComponent;

    if (!documentForm) {
      if (attempts < 20 && !this.componentDestroyed) {
        window.setTimeout(() => this.openAiDocumentInEditor(documentId, attempts + 1), 50);
      }
      return;
    }

    if (documentsList) documentsList.loadDocuments();
    this.showDocumentFormTab();
    documentForm.editDocument(documentId);
    if (!this.componentDestroyed) {
      this.changeDetectorRef.detectChanges();
    }
  }

  // Functions for UI manipulation using vanilla JS or jQuery

  /**
   * Maintain sidebar height correctly on scroll/resize
   */
  content_sidebar_scroll() {
    var content_main = document.getElementById('content-main');
    var content_sidebar = document.getElementById('content-sidebar');
    var sidebar_height = window.innerHeight;
    if (content_sidebar && content_main) {
      if (content_main.getBoundingClientRect().top > 19) {
        content_sidebar.style.position = 'absolute';
        content_sidebar.style.top = content_main.offsetTop + 10 + 'px';
        sidebar_height -= content_main.getBoundingClientRect().top + 40;
        //console.log('greater ' + matter_sidebar.style.top);
      } else {
        content_sidebar.style.position = 'fixed';
        content_sidebar.style.top = '20px';
        sidebar_height -= content_sidebar.getBoundingClientRect().top + 40;
        //console.log('lesser ' + matter_sidebar.style.top);
      }
      if (content_sidebar.clientHeight != sidebar_height) content_sidebar.style.height = sidebar_height + 'px';
    }
  }

  /**
   * Scroll to a position in the sidebar
   * @param {[type]} parentElem [description]
   * @param {[type]} childElem  [description]
   */
  scrollToSidebar(parentElem: Element, childElem: Element) {
    if (parentElem && childElem) {
      let childTopPosition = childElem.getBoundingClientRect().top;
      parentElem.scrollTop = childTopPosition - 50;
    }
  }

  /**
   * Clean test data for matter 697 (file 10711) - admin only
   * Deletes oldest 90% of histories, documents, and accounting data
   */
  cleanTestData() {
    if (!confirm('This will delete 90% of histories, documents, and accounting data. Continue?')) {
      return;
    }
    this.matterService.cleanTestData(this.matter_id).then((response) => {
      if (response.success) {
        this.notyService.notify(response.text, 'success');
        this.loadMatterPage();
      } else {
        this.notyService.notify(response.text, 'error');
      }
    }).catch((error) => {
      this.notyService.notify('Failed to clean test data', 'error');
    });
  }
}
